import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import axios from '@pepper/utils/src/function/axiosInstance'

import { DecodedOrderFilterParams } from 'components/items/FilterParams'

type Props = {
  companyId: string
  pageSize: number
  queryParams?: DecodedOrderFilterParams
  projectId?: string
  statusFilterOverride?: string
  itemType?: string
  options?: {
    isEnabled?: boolean
  }
}

type ReturnType = {
  records: Assignment[]
  total: number
  totalOrders: number
  isLoading: boolean
  refetch: () => void
}

export const useOrderItems = ({
  companyId,
  pageSize,
  projectId,
  queryParams = {},
  statusFilterOverride,
  options,
}: Props): ReturnType => {
  const page = queryParams?.currentPage ?? 0
  const { stage, rework, vertical, sort, search_title, status, assignee } =
    queryParams
  const [totalOrders, setTotalOrders] = useState(0)

  const _getQueryParams = () => {
    const params: any = {
      company_id: companyId,
      workflow_status_id: status,
      workflow_assignee_id: assignee,
      stage,
      client_rework_count: rework,
      vertical,
      fetch_file_public_permissions: true,
    }

    if (!queryParams) return params

    //adding sort_by: expiring first explicitly when filter pending approval selected
    if (
      stage?.length === 1 &&
      stage?.includes('approval_pending') &&
      !sort?.sort_by
    ) {
      params.sort_by = 'client_deadline'
      params.sort_order = 'asc'
    }

    // Override stage value if applied through quick filters
    if (statusFilterOverride) {
      if (statusFilterOverride === 'approval_pending') {
        params.sort_by = 'client_deadline'
        params.sort_order = 'asc'
      }
      params.stage = statusFilterOverride
    }

    // Set sort params
    if (sort?.sort_by) {
      params.sort_by = sort.sort_by
      params.sort_order = sort.sort_order
    }

    // Set search params
    if (search_title) {
      params.search_title = search_title
    }

    return params
  }

  const fetchOrderItems = async (_projectId?: string) => {
    const _queryParams = _getQueryParams()

    const isAllSelected =
      !_queryParams.stage && !_queryParams.rework && !_queryParams.vertical

    const _params = {
      ..._queryParams,
      company_id: companyId,
      page_size: pageSize,
      current_page: page,
      item_types: 'assignment',
      project_id: _projectId,
    }

    if (!_queryParams.stage) {
      _params.stage = 'all'
    }

    const { data: _data } = await axios.get('/assignments', {
      params: _params,
    })
    if (isAllSelected) {
      setTotalOrders(_data?.total_records)
    }
    return _data
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['order-items', projectId ?? 'all', queryParams],
    queryFn: () => fetchOrderItems(projectId),
    enabled: options?.isEnabled,
  })

  return {
    records: data?.records ?? [],
    total: data?.total_records ?? 0,
    totalOrders,
    isLoading,
    refetch,
  }
}
